import { Menu, MenuItem } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { ChatControlButton } from "../../../ChatControlButton";
import { ChatHeaderContainer } from "../../../ChatHeaderContainer";
import { ReactComponent as CollapseIcon } from "./assets/collapse.svg";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";
import { ReactComponent as LogoIcon } from "./assets/logo.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as RestartIcon } from "./assets/restart.svg";
import { ReactComponent as SoundOffIcon } from "./assets/sound-off.svg";
import { ReactComponent as SoundOnIcon } from "./assets/sound-on.svg";

export interface ChatHeaderProps {
  title: string;
  imageUrl?: string;
  isExpanded?: boolean;
  isSoundOn?: boolean;
  onExpandToggle?: () => void;
  onSoundToggle?: () => void;
  onRestart?: () => void;
  onClose?: () => void;
}

export const ChatHeader: FC<ChatHeaderProps> = ({
  title,
  imageUrl,
  isExpanded = false,
  isSoundOn = true,
  onExpandToggle,
  onSoundToggle,
  onRestart,
  onClose,
}) => {
  const { t } = useTranslations();

  const menuItems = useMemo<MenuItem[]>(
    () => [
      {
        id: "expand-toggle",
        label: isExpanded
          ? t({
              id: "chat-bot-ui.menu-option.collapse",
              defaultMessage: "Collapse",
            })
          : t({
              id: "chat-bot-ui.menu-option.expand",
              defaultMessage: "Expand",
            }),
        icon: isExpanded ? <CollapseIcon /> : <ExpandIcon />,
        onSelect: () => onExpandToggle?.(),
        isHidden: !onExpandToggle,
      },
      {
        id: "restart",
        label: t({
          id: "chat-bot-ui.menu-option.start-new-chat",
          defaultMessage: "Start new chat",
        }),
        icon: <RestartIcon />,
        onSelect: () => onRestart?.(),
        isHidden: !onRestart,
      },
      {
        id: "sound-toggle",
        label: isSoundOn
          ? t({
              id: "chat-bot-ui.menu-option.mute-sound",
              defaultMessage: "Mute sound",
            })
          : t({
              id: "chat-bot-ui.menu-option.unmute-sound",
              defaultMessage: "Unmute sound",
            }),
        icon: isSoundOn ? <SoundOffIcon /> : <SoundOnIcon />,
        onSelect: () => onSoundToggle?.(),
        isHidden: !onSoundToggle,
      },
    ],
    [isExpanded, isSoundOn, onExpandToggle, onRestart, onSoundToggle, t]
  );

  const visibleMenuItemsCount = useMemo(
    () => menuItems.filter((item) => !item.isHidden).length,
    [menuItems]
  );

  return (
    <ChatHeaderContainer>
      <div className="flex items-center gap-3 overflow-hidden">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-solid border-dark-200 bg-[#FAF8FA]">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <LogoIcon className="h-7 w-7" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 overflow-hidden">
          <span
            className="truncate font-secondary text-[15px] font-medium text-dark"
            title={title}
          >
            {title}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {visibleMenuItemsCount > 0 && (
          <Menu
            placement="bottom"
            autoClose
            sections={[menuItems]}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => (
              <Trigger
                as={ChatControlButton}
                ref={triggerRef}
                isActive={isOpen}
              >
                <MoreIcon />
              </Trigger>
            )}
          />
        )}
        {onClose && (
          <ChatControlButton size="md" onClick={onClose}>
            <CrossIcon />
          </ChatControlButton>
        )}
      </div>
    </ChatHeaderContainer>
  );
};
